<template>
  <div class="customer-available-time-slots">
    <v-card class="mb-4">
      <v-card-title class="pb-2">
        <v-icon class="mr-2">mdi-clock-outline</v-icon>
        Available Time Slots
      </v-card-title>

      <v-card-text v-if="!timeSlots || timeSlots.length === 0" class="text-center py-8">
        <v-icon size="48" color="grey lighten-1" class="mb-2">mdi-calendar-blank</v-icon>
        <p class="text-body-2 grey--text">No available time slots configured</p>
      </v-card-text>

      <v-card-text v-else>
        <div class="weekday-section" v-for="(daySlots, weekday) in groupedTimeSlots" :key="weekday">
          <div class="weekday-header mb-3">
            <h3 class="text-subtitle-1 font-weight-bold text-capitalize">
              {{ formatWeekday(weekday) }}
            </h3>
            <v-divider class="mt-1"></v-divider>
          </div>

          <div class="time-slots-grid">
            <v-chip
              v-for="(slot, index) in daySlots"
              :key="`${weekday}-${index}`"
              class="ma-1 time-slot-chip"
              color="primary"
              outlined
              small
            >
              <v-icon small class="mr-1">mdi-clock</v-icon>
              {{ formatTimeRange(slot.startTime, slot.endTime) }}
              <template v-if="getDistrictFromSlot(slot)">
                <v-icon small class="ml-2 mr-1">mdi-map-marker</v-icon>
                <span class="location-text">{{ getDistrictFromSlot(slot) }}</span>
              </template>
            </v-chip>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { districtsOptions } from '@/data/optionsConstants';
export default {
  name: 'CustomerAvailableTimeSlots',
  props: {
    timeSlots: {
      type: Array,
      default: () => [],
      validator: slots => {
        if (!Array.isArray(slots)) return false;
        return slots.every(slot => {
          return (
            slot &&
            typeof slot.startTime === 'string' &&
            typeof slot.endTime === 'string' &&
            typeof slot.weekday === 'string' &&
            /^([01]\d|2[0-3]):([0-5]\d)$/.test(slot.startTime) &&
            /^([01]\d|2[0-3]):([0-5]\d)$/.test(slot.endTime)
          );
        });
      },
    },
  },
  computed: {
    groupedTimeSlots() {
      if (!this.timeSlots || !Array.isArray(this.timeSlots)) {
        return {};
      }

      const weekdayOrder = [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
      ];

      const grouped = this.timeSlots.reduce((acc, slot) => {
        const weekday = slot.weekday.toLowerCase();
        if (!acc[weekday]) {
          acc[weekday] = [];
        }
        acc[weekday].push(slot);
        return acc;
      }, {});

      // Sort time slots within each day by start time
      Object.keys(grouped).forEach(weekday => {
        grouped[weekday].sort((a, b) => {
          return a.startTime.localeCompare(b.startTime);
        });
      });

      // Return ordered object based on weekday order
      const orderedGrouped = {};
      weekdayOrder.forEach(day => {
        if (grouped[day]) {
          orderedGrouped[day] = grouped[day];
        }
      });

      return orderedGrouped;
    },
  },
  methods: {
    formatWeekday(weekday) {
      const weekdayMap = {
        monday: 'Monday',
        tuesday: 'Tuesday',
        wednesday: 'Wednesday',
        thursday: 'Thursday',
        friday: 'Friday',
        saturday: 'Saturday',
        sunday: 'Sunday',
      };
      return weekdayMap[weekday.toLowerCase()] || weekday;
    },

    formatTimeRange(startTime, endTime) {
      return `${this.formatTime(startTime)} - ${this.formatTime(endTime)}`;
    },

    formatTime(time) {
      // Convert 24-hour format to 12-hour format
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const period = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${period}`;
    },
    getDistrictFromSlot(slot) {
      const gps = slot && slot.gps;
      if (!gps || typeof gps.latitude !== 'number' || typeof gps.longitude !== 'number') {
        return null;
      }
      let closest = null;
      let minDist = Infinity;
      for (const d of districtsOptions) {
        const dy = gps.latitude - d.lat;
        const dx = gps.longitude - d.long;
        const dist = dy * dy + dx * dx;
        if (dist < minDist) {
          minDist = dist;
          closest = d;
        }
      }
      return closest ? closest.label : null;
    },
  },
};
</script>

<style scoped lang="scss">
.customer-available-time-slots {
  .weekday-section {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .weekday-header {
    margin-bottom: 12px;

    h3 {
      color: rgba(0, 0, 0, 0.87);
      margin-bottom: 4px;
    }
  }

  .time-slots-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .time-slot-chip {
    .location-text {
      font-size: 0.75rem;
      opacity: 0.8;
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
